import { Venue, VenueFetch, VenuesParams } from "../../models/venues";
import { mapVenue } from "../../utils/mappers";
import { api } from "./api";

const normalizeEntityId = (id: string | number) => String(id);

const venueTag = (id: string | number) => ({
  type: "venues" as const,
  id: normalizeEntityId(id),
});

const venueListTag = { type: "venues" as const, id: "LIST" };

type CreateVenueResponse = {
  id: number;
  message?: string;
};

/** Clave de caché estable: omite filtros vacíos para que upsert/refetch coincidan con la lista. */
export const normalizeVenuesQueryArgs = (params: VenuesParams = {}): VenuesParams => {
  const normalized: VenuesParams = {};

  if (params.name?.trim()) {
    normalized.name = params.name.trim();
  }
  if (params.tenantId !== undefined) {
    normalized.tenantId = params.tenantId;
  }
  if (params.status) {
    normalized.status = params.status;
  }
  if (params.limit != null) {
    normalized.limit = params.limit;
  }
  if (params.page != null) {
    normalized.page = params.page;
  }

  return normalized;
};

const normalizeVenueListResponse = (response: unknown): unknown[] => {
  if (Array.isArray(response)) return response;

  if (typeof response !== "object" || response === null) return [];

  const record = response as Record<string, unknown>;
  const { data } = record;

  if (Array.isArray(data)) return data;

  if (typeof data === "object" && data !== null) {
    const container = data as Record<string, unknown>;
    if (Array.isArray(container.items)) return container.items;
    if (Array.isArray(container.events)) return container.events;
  }

  if (Array.isArray(record.items)) return record.items;

  return [];
};

const normalizeVenueResponse = (response: unknown): unknown => {
  if (typeof response !== "object" || response === null) return response;

  const record = response as Record<string, unknown>;
  if (record.success === true && record.data !== undefined) {
    return record.data;
  }

  return response;
};

const mapVenueList = (response: unknown): Venue[] =>
  normalizeVenueListResponse(response).map((item) => mapVenue(item));

const listProvidesTags = (result: Venue[] | undefined) =>
  result
    ? [...result.map(({ id }) => venueTag(id)), venueListTag]
    : [venueListTag];

type VenueListEndpoint = "getVenues" | "getActiveVenues";

type EbApiQueryEntry = {
  endpointName?: string;
  originalArgs?: VenuesParams | { tenantId?: string | null };
};

const patchAllVenueListCaches = (
  dispatch: (action: unknown) => unknown,
  getState: () => unknown,
  updater: (draft: Venue[]) => void
): Array<{ undo: () => void }> => {
  const undoPatches: Array<{ undo: () => void }> = [];
  const state = getState() as { ebAPI?: { queries?: Record<string, EbApiQueryEntry> } };
  const queries = state.ebAPI?.queries ?? {};

  for (const query of Object.values(queries)) {
    if (query.endpointName !== "getVenues" && query.endpointName !== "getActiveVenues") {
      continue;
    }
    if (query.originalArgs === undefined) {
      continue;
    }

    try {
      const patch = dispatch(
        venuesApi.util.updateQueryData(
          query.endpointName as VenueListEndpoint,
          query.originalArgs as VenuesParams,
          updater
        )
      ) as { undo?: () => void };

      if (patch?.undo) {
        undoPatches.push({ undo: patch.undo });
      }
    } catch {
      // Sin entrada en caché para esos args.
    }
  }

  return undoPatches;
};

export const applyVenueListOverrides = (
  venues: Venue[] | undefined,
  options?: {
    createdVenue?: Venue;
    removedVenueIds?: number[];
  }
): Venue[] => {
  const removedIds = new Set(options?.removedVenueIds ?? []);
  let list = (venues ?? []).filter((venue) => !removedIds.has(venue.id));

  const createdVenue = options?.createdVenue;
  if (createdVenue?.id && !list.some((venue) => venue.id === createdVenue.id)) {
    list = [createdVenue, ...list];
  }

  return list;
};

/** @deprecated Usar applyVenueListOverrides */
export const mergeCreatedVenueIntoList = (
  venues: Venue[] | undefined,
  createdVenue?: Venue
): Venue[] => applyVenueListOverrides(venues, { createdVenue });

export const venuesApi = api.injectEndpoints({
  endpoints: (build) => ({
    getVenues: build.query<Venue[], VenuesParams>({
      query: (params) => ({
        url: "/bo/venues",
        params: normalizeVenuesQueryArgs(params),
      }),
      serializeQueryArgs: ({ endpointName, queryArgs }) => {
        const normalized = normalizeVenuesQueryArgs(queryArgs);
        return `${endpointName}(${JSON.stringify(normalized)})`;
      },
      transformResponse: (response: unknown) => mapVenueList(response),
      providesTags: (result) => listProvidesTags(result),
    }),

    getVenueById: build.query<Venue, string | number>({
      query: (id) => ({
        url: `/bo/venues/${id}`,
      }),
      transformResponse: (response: unknown) => mapVenue(normalizeVenueResponse(response)),
      providesTags: (_result, _error, id) => [venueTag(id)],
    }),

    getActiveVenues: build.query<Venue[], { tenantId?: string | null }>({
      query: (params) => ({
        url: "/bo/venues",
        params: { ...normalizeVenuesQueryArgs(params), status: "ACTIVE" },
      }),
      transformResponse: (response: unknown) => mapVenueList(response),
      providesTags: (result) => listProvidesTags(result),
    }),

    createVenue: build.mutation<CreateVenueResponse, VenueFetch>({
      query: (data) => ({
        url: "/bo/venues",
        method: "POST",
        body: data,
      }),
      transformResponse: (response: unknown): CreateVenueResponse => {
        const data = normalizeVenueResponse(response) as CreateVenueResponse;
        return data;
      },
      invalidatesTags: (result) => [
        venueListTag,
        ...(result?.id != null ? [venueTag(result.id)] : []),
      ],
    }),

    updateVenue: build.mutation<void, VenueFetch>({
      query: (data) => ({
        url: `/bo/venues/${data.id}`,
        method: "PATCH",
        body: data,
      }),
      invalidatesTags: (_result, _error, { id }) => [
        ...(id != null ? [venueTag(id)] : []),
        venueListTag,
      ],
    }),

    deleteVenue: build.mutation<void, number>({
      query: (id) => ({
        url: `/bo/venues/${id}`,
        method: "DELETE",
      }),
      async onQueryStarted(id, { dispatch, queryFulfilled, getState }) {
        const undoPatches = patchAllVenueListCaches(dispatch, getState, (draft) => {
          const index = draft.findIndex((venue) => venue.id === id);
          if (index !== -1) {
            draft.splice(index, 1);
          }
        });

        try {
          await queryFulfilled;
        } catch {
          undoPatches.forEach((patch) => patch.undo());
        }
      },
      invalidatesTags: (_result, _error, id) => [venueTag(id)],
    }),

    getVenueComments: build.query<any[], string | number>({
      query: (id) => ({
        url: `/bo/venues/${id}/comments`,
      }),
      transformResponse: (response: unknown) => {
        const data = normalizeVenueResponse(response);
        if (Array.isArray(data)) return data;
        return [];
      },
    }),
  }),
});

export const {
  useGetVenuesQuery,
  useGetVenueByIdQuery,
  useGetActiveVenuesQuery,
  useCreateVenueMutation,
  useUpdateVenueMutation,
  useDeleteVenueMutation,
  useGetVenueCommentsQuery,
} = venuesApi;
